"use client"

import type { LucideIcon } from "lucide-react"

import { cn } from "@/common/lib/utils"

/** Shared small presentation pieces, so the views stay visually one app. */

/**
 * Semantic tone → the validated palette, NOT Tailwind's `emerald-*`/`amber-*`.
 * Those utilities were a second, slightly different definition of the same
 * meanings (`emerald-600` is #059669, `--lp-good` is #15803d) and they bypass
 * the CVD validation documented in `charts/palette.ts`.
 */
const TONE_VAR = {
    warn: "var(--lp-warning)",
    danger: "var(--lp-critical)",
    good: "var(--lp-good)",
} as const

export type Tone = keyof typeof TONE_VAR | "neutral" | "info"

/**
 * The one chip. The demo had 22 separate declarations of this shape with four
 * radii, three sizes and six padding pairs; they are the same object.
 */
export function Chip({
    tone = "neutral",
    icon: Icon,
    className,
    children,
}: {
    tone?: Tone
    icon?: LucideIcon
    className?: string
    children: React.ReactNode
}) {
    const colour = tone === "neutral" || tone === "info" ? undefined : TONE_VAR[tone]
    return (
        <span
            className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium",
                tone === "neutral" && "bg-muted text-muted-foreground",
                tone === "info" && "bg-primary/10 text-primary",
                className,
            )}
            style={colour ? { color: colour, background: `color-mix(in srgb, ${colour} 12%, transparent)` } : undefined}
        >
            {Icon && <Icon className="size-3" />}
            {children}
        </span>
    )
}

/**
 * The view banner. Was copy-pasted verbatim into all five views, and one copy
 * had already drifted (integrations dropped the flex prefix, steuerung used
 * `text-lg` where the others used `text-xl`).
 *
 * It carries the page's `h1`: the shell used to render a generic "Lernpfad"
 * heading above every view, whose subtitle described the *employee* view even
 * on the org and control screens. Two headers, one of them wrong.
 */
export function ViewHeader({
    icon: Icon,
    title,
    subtitle,
    lead,
    action,
    children,
}: {
    icon?: LucideIcon
    title: string
    subtitle?: React.ReactNode
    /** Rendered left of the title — the employee view's avatar. */
    lead?: React.ReactNode
    /** Rendered right, and pushed to the far edge. */
    action?: React.ReactNode
    children?: React.ReactNode
}) {
    return (
        <div className="lp-card flex flex-wrap items-end justify-between gap-4 rounded-xl border bg-gradient-to-br from-primary/[0.06] to-transparent p-5">
            <div className="flex items-center gap-4">
                {lead}
                <div>
                    <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
                        {Icon && <Icon className="size-5 text-primary" />}
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{subtitle}</p>
                    )}
                    {children}
                </div>
            </div>
            {action}
        </div>
    )
}

export function Stat({
    icon: Icon,
    label,
    value,
    hint,
    tone = "neutral",
}: {
    icon?: LucideIcon
    label: string
    value: string
    hint?: string
    tone?: "neutral" | "warn" | "danger" | "good"
}) {
    return (
        <div className="lp-card rounded-xl border bg-card p-4 lp-card-hover">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {Icon && <Icon className="size-3.5" />}
                {label}
            </div>
            <p
                className="mt-2 text-2xl font-semibold tabular-nums"
                style={tone === "neutral" ? undefined : { color: TONE_VAR[tone] }}
            >
                {value}
            </p>
            {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
        </div>
    )
}

export function SectionHeading({
    icon: Icon,
    title,
    count,
    action,
}: {
    icon?: LucideIcon
    title: string
    count?: number
    action?: React.ReactNode
}) {
    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b pb-2">
            <h3 className="flex items-center gap-2 font-semibold">
                {Icon && <Icon className="size-4 text-muted-foreground" />}
                {title}
                {count !== undefined && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal tabular-nums text-muted-foreground">
                        {count}
                    </span>
                )}
            </h3>
            {action}
        </div>
    )
}
